import {
  fetchCustomerIdsWithBookingsInPeriods,
  type BookingExcludePeriod,
} from '../lib/campaignBookingExclude';
import { unionSegmentCustomerIds } from '../lib/customers/segments';
import { supabase } from '../lib/supabaseClient';
import { 
  normalizePhoneNumber,
  normalizeEmail 
} from './secondaryContactLists';

export interface RecipientFilters {
  tags?: string[];
  status?: string;
  source?: string | null;
  hasEmail?: boolean;
  minSpent?: number | null;
  createdAfter?: string | null;
  createdBefore?: string | null;
  lastInteractionAfter?: string | null;
  optedInOnly?: boolean;
  includeTags?: string[];
  excludeTags?: string[];
  manualCustomerIds?: string[];
  /** Exclude customers who have any booking overlapping these date ranges (YYYY-MM-DD). */
  excludeBookingPeriods?: BookingExcludePeriod[];
  /** Customer segments to include. Several segments are combined. Tag and booking filters still apply on top. */
  segmentIds?: string[];
}

export interface CampaignRecipient {
  customer_id: string | null;
  email?: string; // Optional for SMS campaigns
  phone?: string; // Required for SMS campaigns
  first_name: string | null;
  last_name: string | null;
  variant_assigned?: string | null;
}

/**
 * Build recipient list for an email or SMS campaign based on filters
 */
export async function buildCampaignRecipientList(
  businessId: string,
  recipientType: 'all_customers' | 'segment',
  recipientFilters?: RecipientFilters,
  channelType: 'email' | 'sms' = 'email'
): Promise<CampaignRecipient[]> {
  try {
    // If manual customer IDs are provided, use those directly
    if (recipientFilters?.manualCustomerIds && recipientFilters.manualCustomerIds.length > 0) {
      // Fetch in batches if there are many customer IDs (Supabase .in() has limits)
      const BATCH_SIZE = 1000;
      let allCustomers: any[] = [];
      const customerIds = recipientFilters.manualCustomerIds;

      for (let i = 0; i < customerIds.length; i += BATCH_SIZE) {
        const batchIds = customerIds.slice(i, i + BATCH_SIZE);
        const selectFields = channelType === 'sms' 
          ? 'id, phone, first_name, last_name, promotional_offers, Tags'
          : 'id, email, first_name, last_name, promotional_offers, Tags';
        
        let query = supabase
          .from('customers')
          .select(selectFields)
          .eq('business_id', businessId)
          .in('id', batchIds);
        
        // For email, require email address; for SMS, require phone number
        if (channelType === 'email') {
          query = query.not('email', 'is', null).neq('email', '');
        } else {
          query = query.not('phone', 'is', null).neq('phone', '');
        }
        
        const { data: customers, error } = await query;

        if (error) {
          console.error('Error fetching manual selected customers:', error);
          throw error;
        }

        if (customers) {
          allCustomers = [...allCustomers, ...customers];
        }
      }

      const customers = allCustomers;

      // Customers are already filtered (unsubscribed excluded automatically)
      let filteredCustomers = customers || [];

      if (recipientFilters.segmentIds && recipientFilters.segmentIds.length > 0) {
        const allowed = await unionSegmentCustomerIds(businessId, recipientFilters.segmentIds);
        filteredCustomers = filteredCustomers.filter((customer) => allowed.has(customer.id));
      }

      // Apply tag filters if specified (for manual selection with additional tag filters)
      if (recipientFilters.includeTags && recipientFilters.includeTags.length > 0) {
        filteredCustomers = filteredCustomers.filter(customer => {
          const customerTags = customer.Tags || [];
          const customerTagNames = customerTags.map((tag: any) => 
            typeof tag === 'string' ? tag : (tag?.name || tag)
          );
          return recipientFilters.includeTags!.some(includeTag => customerTagNames.includes(includeTag));
        });
      }

      if (recipientFilters.excludeTags && recipientFilters.excludeTags.length > 0) {
        filteredCustomers = filteredCustomers.filter(customer => {
          const customerTags = customer.Tags || [];
          const customerTagNames = customerTags.map((tag: any) => 
            typeof tag === 'string' ? tag : (tag?.name || tag)
          );
          return !recipientFilters.excludeTags!.some(excludeTag => customerTagNames.includes(excludeTag));
        });
      }

      if (recipientFilters.excludeBookingPeriods?.length) {
        const bookedCustomerIds = await fetchCustomerIdsWithBookingsInPeriods(
          businessId,
          recipientFilters.excludeBookingPeriods
        );
        filteredCustomers = filteredCustomers.filter(
          (customer) => !bookedCustomerIds.has(customer.id)
        );
      }

      // Get unsubscribed emails to exclude - fetch in batches
      const UNSUBSCRIBE_BATCH_SIZE = 1000;
      let allUnsubscribed: any[] = [];
      let unsubscribeOffset = 0;
      let hasMoreUnsubscribed = true;

      while (hasMoreUnsubscribed) {
        const { data: unsubscribed, error: unsubscribedError } = await supabase
          .from('email_unsubscribes')
          .select('email')
          .eq('business_id', businessId)
          .eq('is_active', true)
          .range(unsubscribeOffset, unsubscribeOffset + UNSUBSCRIBE_BATCH_SIZE - 1);

        if (unsubscribedError) {
          console.error('Error fetching unsubscribed emails:', unsubscribedError);
          break;
        }

        if (unsubscribed && unsubscribed.length > 0) {
          allUnsubscribed = [...allUnsubscribed, ...unsubscribed];
          unsubscribeOffset += UNSUBSCRIBE_BATCH_SIZE;
          hasMoreUnsubscribed = unsubscribed.length === UNSUBSCRIBE_BATCH_SIZE;
        } else {
          hasMoreUnsubscribed = false;
        }
      }

      const unsubscribedEmails = new Set(
        allUnsubscribed.map(u => u.email?.toLowerCase()).filter(Boolean)
      );

      // Filter out unsubscribed customers and format recipients
      // Deduplicate by email/phone (case-insensitive for email)
      const seenContacts = new Set<string>();
      const recipients: CampaignRecipient[] = filteredCustomers
        .filter(c => {
          if (channelType === 'email') {
            const email = c.email?.toLowerCase();
            if (!email || unsubscribedEmails.has(email)) return false;
            // Check for duplicates
            if (seenContacts.has(email)) return false;
            seenContacts.add(email);
            return true;
          } else {
            const phone = c.phone;
            if (!phone) return false;
            // Check for duplicates
            if (seenContacts.has(phone)) return false;
            seenContacts.add(phone);
            return true;
          }
        })
        .map(c => ({
          customer_id: c.id,
          ...(channelType === 'email' 
            ? { email: c.email! }
            : { phone: c.phone! }
          ),
          first_name: c.first_name,
          last_name: c.last_name
        }));

      return recipients;
    }

    // Helper function to build the base query with all filters
    const buildQuery = () => {
      const selectFields = channelType === 'sms'
        ? 'id, phone, first_name, last_name, promotional_offers, Tags'
        : 'id, email, first_name, last_name, promotional_offers, Tags';
      
      let query = supabase
        .from('customers')
        .select(selectFields)
        .eq('business_id', businessId);
      
      // For email, require email address; for SMS, require phone number
      if (channelType === 'email') {
        query = query.not('email', 'is', null).neq('email', '');
      } else {
        query = query.not('phone', 'is', null).neq('phone', '');
      }

      // Apply filters if segment type
      if (recipientType === 'segment' && recipientFilters) {
        // Filter by tags if any selected (legacy tags array)
        if (recipientFilters.tags && recipientFilters.tags.length > 0) {
          query = query.contains('tags', recipientFilters.tags);
        }

        // Filter by status
        if (recipientFilters.status) {
          query = query.eq('status', recipientFilters.status);
        } else {
          // Default to active if no status specified
          query = query.eq('status', 'active');
        }

        // Filter by source
        if (recipientFilters.source) {
          query = query.eq('source', recipientFilters.source);
        }

        // Date filters
        if (recipientFilters.createdAfter) {
          query = query.gte('created_at', recipientFilters.createdAfter);
        }
        if (recipientFilters.createdBefore) {
          query = query.lte('created_at', recipientFilters.createdBefore);
        }
        if (recipientFilters.lastInteractionAfter) {
          query = query.gte('last_interaction', recipientFilters.lastInteractionAfter);
        }

        // Minimum spent filter (would need to join with payments/bookings)
        // For now, we'll skip this as it requires more complex querying
        // Can be added later if needed
      } else {
        // All customers - just filter by active status and email
        query = query.eq('status', 'active');
      }

      return query;
    };

    // Get all matching customers - fetch in batches
    const BATCH_SIZE = 1000;
    let allCustomers: any[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const query = buildQuery();
      const { data: customers, error } = await query.range(offset, offset + BATCH_SIZE - 1);

      if (error) {
        console.error('Error fetching campaign recipients:', error);
        throw error;
      }

      if (customers && customers.length > 0) {
        allCustomers = [...allCustomers, ...customers];
        offset += BATCH_SIZE;
        hasMore = customers.length === BATCH_SIZE;
      } else {
        hasMore = false;
      }
    }

    const customers = allCustomers;

    if (!customers || customers.length === 0) {
      return [];
    }

    // Apply tag filters if specified (include/exclude tags)
    let filteredCustomers = customers;

    if (recipientFilters?.segmentIds && recipientFilters.segmentIds.length > 0) {
      const allowed = await unionSegmentCustomerIds(businessId, recipientFilters.segmentIds);
      filteredCustomers = filteredCustomers.filter((customer) => allowed.has(customer.id));
    }
    
    if (recipientFilters?.includeTags && recipientFilters.includeTags.length > 0) {
      filteredCustomers = filteredCustomers.filter(customer => {
        const customerTags = customer.Tags || [];
        const customerTagNames = customerTags.map((tag: any) => 
          typeof tag === 'string' ? tag : (tag?.name || tag)
        );
        return recipientFilters.includeTags!.some(includeTag => customerTagNames.includes(includeTag));
      });
    }

    if (recipientFilters?.excludeTags && recipientFilters.excludeTags.length > 0) {
      filteredCustomers = filteredCustomers.filter(customer => {
        const customerTags = customer.Tags || [];
        const customerTagNames = customerTags.map((tag: any) => 
          typeof tag === 'string' ? tag : (tag?.name || tag)
        );
        return !recipientFilters.excludeTags!.some(excludeTag => customerTagNames.includes(excludeTag));
      });
    }

    if (recipientFilters?.excludeBookingPeriods?.length) {
      const bookedCustomerIds = await fetchCustomerIdsWithBookingsInPeriods(
        businessId,
        recipientFilters.excludeBookingPeriods
      );
      filteredCustomers = filteredCustomers.filter(
        (customer) => !bookedCustomerIds.has(customer.id)
      );
    }

    // Get unsubscribed emails to exclude - fetch in batches
    const UNSUBSCRIBE_BATCH_SIZE = 1000;
    let allUnsubscribed: any[] = [];
    let unsubscribeOffset = 0;
    let hasMoreUnsubscribed = true;

    while (hasMoreUnsubscribed) {
      const { data: unsubscribed, error: unsubscribedError } = await supabase
        .from('email_unsubscribes')
        .select('email')
        .eq('business_id', businessId)
        .eq('is_active', true)
        .range(unsubscribeOffset, unsubscribeOffset + UNSUBSCRIBE_BATCH_SIZE - 1);

      if (unsubscribedError) {
        console.error('Error fetching unsubscribed emails:', unsubscribedError);
        break;
      }

      if (unsubscribed && unsubscribed.length > 0) {
        allUnsubscribed = [...allUnsubscribed, ...unsubscribed];
        unsubscribeOffset += UNSUBSCRIBE_BATCH_SIZE;
        hasMoreUnsubscribed = unsubscribed.length === UNSUBSCRIBE_BATCH_SIZE;
      } else {
        hasMoreUnsubscribed = false;
      }
    }

    const unsubscribedEmails = new Set(
      allUnsubscribed.map(u => u.email?.toLowerCase()).filter(Boolean)
    );

    // Filter out unsubscribed customers and format recipients
    // Deduplicate by email/phone (case-insensitive for email)
    const seenContacts = new Set<string>();
    const customerRecipients: CampaignRecipient[] = filteredCustomers
      .filter(c => {
        if (channelType === 'email') {
          const email = c.email?.toLowerCase();
          if (!email || unsubscribedEmails.has(email)) return false;
          // Check for duplicates
          if (seenContacts.has(email)) return false;
          seenContacts.add(email);
          return true;
        } else {
          const phone = c.phone;
          if (!phone) return false;
          // Normalize phone for duplicate checking
          const normalizedPhone = normalizePhoneNumber(phone);
          // Check for duplicates
          if (seenContacts.has(normalizedPhone)) return false;
          seenContacts.add(normalizedPhone);
          return true;
        }
      })
      .map(c => ({
        customer_id: c.id,
        ...(channelType === 'email'
          ? { email: normalizeEmail(c.email!) }
          : { phone: normalizePhoneNumber(c.phone!) }
        ),
        first_name: c.first_name,
        last_name: c.last_name
      }));

    return customerRecipients;
  } catch (error) {
    console.error('Error building campaign recipient list:', error);
    throw error;
  }
}

/**
 * Shuffle an array in-place (Fisher-Yates) and return it.
 */
function shuffleArray<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Create recipient records in email_campaign_recipients table.
 * If variantNames is provided (e.g. ['a','b','c']), recipients are randomly
 * assigned to variants with an equal split and variant_assigned is stored.
 */
export async function createCampaignRecipients(
  campaignId: string,
  recipients: CampaignRecipient[],
  variantNames?: string[]
): Promise<void> {
  try {
    if (recipients.length === 0) {
      return;
    }

    // Deduplicate recipients by email (for email campaigns) or phone (for SMS campaigns)
    const seenContacts = new Set<string>();
    const uniqueRecipients = recipients.filter(recipient => {
      if (recipient.email) {
        const email = recipient.email.toLowerCase();
        if (seenContacts.has(`email:${email}`)) return false;
        seenContacts.add(`email:${email}`);
        return true;
      }
      if (recipient.phone) {
        if (seenContacts.has(`phone:${recipient.phone}`)) return false;
        seenContacts.add(`phone:${recipient.phone}`);
        return true;
      }
      return false;
    });

    // Assign variants with equal random split if variant names provided
    if (variantNames && variantNames.length >= 2) {
      const shuffled = shuffleArray([...uniqueRecipients]);
      for (let i = 0; i < shuffled.length; i++) {
        shuffled[i].variant_assigned = variantNames[i % variantNames.length];
      }
    }

    // Prepare recipient records
    const recipientRecords = uniqueRecipients.map(recipient => ({
      campaign_id: campaignId,
      customer_id: recipient.customer_id,
      email: recipient.email || null,
      phone: recipient.phone || null,
      first_name: recipient.first_name,
      last_name: recipient.last_name,
      variant_assigned: recipient.variant_assigned || null,
      status: 'pending'
    }));

    // Insert in batches to avoid overwhelming the database
    const batchSize = 100;
    for (let i = 0; i < recipientRecords.length; i += batchSize) {
      const batch = recipientRecords.slice(i, i + batchSize);
      const { error } = await supabase
        .from('email_campaign_recipients')
        .insert(batch);

      if (error) {
        console.error(`Error inserting recipient batch ${i / batchSize + 1}:`, error);
        throw error;
      }
    }
  } catch (error) {
    console.error('Error creating campaign recipients:', error);
    throw error;
  }
}

/**
 * Get recipient count for a campaign (without creating records)
 */
export async function getCampaignRecipientCount(
  businessId: string,
  recipientType: 'all_customers' | 'segment',
  recipientFilters?: RecipientFilters,
  channelType: 'email' | 'sms' = 'email'
): Promise<number> {
  const recipients = await buildCampaignRecipientList(businessId, recipientType, recipientFilters, channelType);
  return recipients.length;
}

export interface RecipientBreakdown {
  customerCount: number;
  secondaryListCount: number;
  total: number;
}

export async function getCampaignRecipientBreakdown(
  businessId: string,
  recipientType: 'all_customers' | 'segment',
  recipientFilters?: RecipientFilters,
  channelType: 'email' | 'sms' = 'email'
): Promise<RecipientBreakdown> {
  const recipients = await buildCampaignRecipientList(businessId, recipientType, recipientFilters, channelType);
  
  const customerCount = recipients.filter(r => r.customer_id !== null).length;
  const secondaryListCount = recipients.filter(r => r.customer_id === null).length;
  
  return {
    customerCount,
    secondaryListCount,
    total: recipients.length
  };
}

export interface SaveCampaignRecordResult {
  campaign: Record<string, unknown>;
  recipientCount: number;
  sendImmediately: boolean;
  scheduledAt: string | null;
  isUpdate: boolean;
}

export interface SequenceStepPayload {
  step_number: number;
  delay_hours: number;
  condition: 'everyone' | 'not_opened' | 'opened_no_click' | 'clicked';
  subject: string;
  html_content: string;
  variants: Array<{ name: string; subject: string; html_content: string }>;
}

/**
 * Persist the follow-up steps of an extended campaign. Each step is its own
 * email_campaigns row (status 'waiting') chained via parent_step_id; recipients
 * are resolved at send time from the parent step's engagement.
 */
async function persistSequenceSteps(
  businessId: string,
  root: Record<string, unknown>,
  steps: SequenceStepPayload[]
): Promise<void> {
  const rootId = root.id as string;

  // Replace any previously staged (not-yet-active) steps for this root.
  // Steps normally carry no billing (they charge at send), but if any ever
  // holds a payment intent, release/refund it BEFORE the row disappears.
  const { data: staleSteps } = await supabase
    .from('email_campaigns')
    .select('id, billing_payment_intent_id')
    .eq('sequence_root_id', rootId)
    .in('status', ['waiting', 'scheduled', 'draft']);
  for (const step of staleSteps || []) {
    if (!step.billing_payment_intent_id) continue;
    const { data: result, error } = await supabase.functions.invoke('cancel-email-campaign', {
      body: { businessId, campaignId: step.id },
    });
    if (error || !(result as any)?.success) {
      throw new Error(
        (result as any)?.error || error?.message || 'Could not release payment on a staged follow-up step'
      );
    }
  }

  await supabase
    .from('email_campaigns')
    .delete()
    .eq('sequence_root_id', rootId)
    .in('status', ['waiting', 'scheduled', 'draft']);

  await supabase
    .from('email_campaigns')
    .update({ is_sequence_root: steps.length > 0 })
    .eq('id', rootId);

  let parentId = rootId;
  for (const step of steps) {
    const externalizedHtml =
      (await externalizeBase64Images(step.html_content, businessId)) || step.html_content;
    const isAbTest = step.variants.length > 0;

    const { data: stepCampaign, error } = await supabase
      .from('email_campaigns')
      .insert({
        business_id: businessId,
        name: `${root.name} — Email ${step.step_number}`,
        channel_type: 'email',
        status: 'waiting',
        campaign_type: isAbTest ? 'ab_test' : 'standard',
        is_ab_test: isAbTest,
        subject: step.subject,
        html_content: externalizedHtml,
        from_email: root.from_email,
        from_name: root.from_name,
        reply_to_email: root.reply_to_email ?? null,
        recipient_type: root.recipient_type || 'all_customers',
        recipient_filters: {},
        recipient_count: 0,
        send_immediately: false,
        timezone: root.timezone || 'UTC',
        sequence_root_id: rootId,
        parent_step_id: parentId,
        step_number: step.step_number,
        step_delay_hours: step.delay_hours,
        step_condition: step.condition,
      })
      .select()
      .single();

    if (error || !stepCampaign) {
      console.error('Error inserting sequence step:', error);
      throw error || new Error('Failed to create follow-up step');
    }

    if (isAbTest) {
      const variantRows = [
        // Variant "a" mirrors the step's own content
        {
          campaign_id: stepCampaign.id,
          variant_name: 'a',
          subject: step.subject,
          html_content: externalizedHtml,
          plain_text_content: null,
          from_name: root.from_name,
          sent_count: 0,
          delivered_count: 0,
          opened_count: 0,
          clicked_count: 0,
          open_rate: 0,
          click_rate: 0,
        },
        ...(await Promise.all(
          step.variants.map(async (v) => ({
            campaign_id: stepCampaign.id,
            variant_name: v.name,
            subject: v.subject,
            html_content:
              (await externalizeBase64Images(v.html_content, businessId)) || v.html_content,
            plain_text_content: null,
            from_name: root.from_name,
            sent_count: 0,
            delivered_count: 0,
            opened_count: 0,
            clicked_count: 0,
            open_rate: 0,
            click_rate: 0,
          }))
        )),
      ];
      const { error: variantError } = await supabase
        .from('email_campaign_variants')
        .insert(variantRows);
      if (variantError) {
        console.error('Error inserting step variants:', variantError);
      }
    }

    parentId = stepCampaign.id;
  }
}

const CAMPAIGN_IMAGE_BUCKET = 'user-uploads';
const BASE64_IMAGE_RE = /data:image\/([a-zA-Z0-9.+-]+);base64,([A-Za-z0-9+/=]+)/g;

function extForImageMime(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes('jpeg') || m === 'jpg') return 'jpg';
  if (m.includes('png')) return 'png';
  if (m.includes('gif')) return 'gif';
  if (m.includes('webp')) return 'webp';
  if (m.includes('svg')) return 'svg';
  return 'png';
}

/** Upload an image blob to public storage and return its URL (or null on failure). */
export async function uploadCampaignImage(
  blob: Blob,
  businessId: string
): Promise<string | null> {
  try {
    const mime = blob.type || 'image/png';
    const ext = extForImageMime(mime.replace('image/', ''));
    const uuid =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const path = `campaign-images/${businessId}/${uuid}.${ext}`;
    const { error } = await supabase.storage
      .from(CAMPAIGN_IMAGE_BUCKET)
      .upload(path, blob, { contentType: mime, upsert: false, cacheControl: '31536000' });
    if (error) {
      console.error('Failed to upload campaign image:', error);
      return null;
    }
    const { data } = supabase.storage.from(CAMPAIGN_IMAGE_BUCKET).getPublicUrl(path);
    return data?.publicUrl || null;
  } catch (err) {
    console.error('Failed to upload campaign image:', err);
    return null;
  }
}

/**
 * Replace inline base64 `data:image` URIs in email HTML with hosted Storage URLs.
 * Keeps `html_content` small (avoids the send worker's memory blowup) and lets
 * images actually render in email clients (which strip base64 data URIs).
 * Best-effort: any image that fails to upload is left as-is.
 */
export async function externalizeBase64Images(
  html: string | null | undefined,
  businessId: string
): Promise<string | null | undefined> {
  if (!html || !html.includes('data:image') || !businessId) return html;

  const uploaded = new Map<string, string>(); // full data URI -> public URL
  const matches = Array.from(html.matchAll(BASE64_IMAGE_RE));

  for (const match of matches) {
    const fullDataUri = match[0];
    if (uploaded.has(fullDataUri)) continue;
    const mime = match[1];
    const base64 = match[2];
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const contentType = `image/${mime.toLowerCase() === 'jpg' ? 'jpeg' : mime}`;
      const blob = new Blob([bytes], { type: contentType });
      const url = await uploadCampaignImage(blob, businessId);
      if (url) uploaded.set(fullDataUri, url);
    } catch (err) {
      console.error('Failed to externalize base64 image:', err);
    }
  }

  let out = html;
  for (const [dataUri, url] of uploaded.entries()) {
    out = out.split(dataUri).join(url);
  }
  return out;
}

/** Persist a campaign draft (create or update) and sync recipients. */
export async function saveCampaignRecord(
  businessId: string,
  campaignData: Record<string, unknown>,
  editingCampaignId?: string | null
): Promise<SaveCampaignRecordResult> {
  const payload = { ...campaignData };
  const variantPayloads = payload._variants as Array<Record<string, unknown>> | undefined;
  delete payload._variants;
  const sequenceSteps = payload._sequenceSteps as SequenceStepPayload[] | undefined;
  delete payload._sequenceSteps;

  const channelType = (payload.channel_type as 'email' | 'sms') || 'email';

  // Host any inline base64 images so html_content stays small (prevents the send
  // worker's memory blowup) and images render in email clients.
  if (channelType === 'email' && typeof payload.html_content === 'string') {
    payload.html_content = await externalizeBase64Images(payload.html_content, businessId);
  }

  const recipients = await buildCampaignRecipientList(
    businessId,
    (payload.recipient_type as 'all_customers' | 'segment') || 'all_customers',
    payload.recipient_filters as RecipientFilters | undefined,
    channelType
  );

  payload.recipient_count = recipients.length;

  const variantNames =
    payload.is_ab_test && variantPayloads
      ? variantPayloads.map((v) => String(v.name))
      : undefined;

  const persistVariants = async (campaignId: string) => {
    if (!payload.is_ab_test || !variantPayloads) return;

    await supabase.from('email_campaign_variants').delete().eq('campaign_id', campaignId);

    const variantRows = await Promise.all(
      variantPayloads.map(async (v) => ({
        campaign_id: campaignId,
        variant_name: v.name,
        subject: v.subject || null,
        html_content:
          channelType === 'email' && typeof v.html_content === 'string'
            ? (await externalizeBase64Images(v.html_content, businessId)) || null
            : v.html_content || null,
        plain_text_content: null,
        sms_content: v.sms_content || null,
        from_name: v.from_name || null,
        sent_count: 0,
        delivered_count: 0,
        opened_count: 0,
        clicked_count: 0,
        open_rate: 0,
        click_rate: 0,
      }))
    );

    const { error: variantError } = await supabase.from('email_campaign_variants').insert(variantRows);
    if (variantError) {
      console.error('Error inserting variants:', variantError);
    }
  };

  let campaign: Record<string, unknown>;

  if (editingCampaignId) {
    const { data: updatedCampaign, error } = await supabase
      .from('email_campaigns')
      .update(payload)
      .eq('id', editingCampaignId)
      .eq('business_id', businessId)
      .select()
      .single();

    if (error) throw error;
    campaign = updatedCampaign as Record<string, unknown>;

    await persistVariants(editingCampaignId);

    if (payload.recipient_type || payload.recipient_filters) {
      await supabase.from('email_campaign_recipients').delete().eq('campaign_id', editingCampaignId);
      if (recipients.length > 0) {
        await createCampaignRecipients(editingCampaignId, recipients, variantNames);
      }
    }
  } else {
    const { data: newCampaign, error } = await supabase
      .from('email_campaigns')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    campaign = newCampaign as Record<string, unknown>;

    await persistVariants(String(campaign.id));

    if (recipients.length > 0) {
      await createCampaignRecipients(String(campaign.id), recipients, variantNames);
    }
  }

  // Extended campaign: persist follow-up step campaigns chained to the root.
  if (channelType === 'email' && sequenceSteps !== undefined) {
    await persistSequenceSteps(businessId, campaign, sequenceSteps);
  }

  return {
    campaign,
    recipientCount: recipients.length,
    sendImmediately: Boolean(payload.send_immediately),
    scheduledAt: (payload.scheduled_at as string) || null,
    isUpdate: Boolean(editingCampaignId),
  };
}

/** Queue an email or SMS campaign for sending (dev mode skips billing). */
export async function queueCampaignSend(
  campaignId: string,
  businessId: string,
  channelType: 'email' | 'sms' = 'email'
): Promise<void> {
  const { error: updateError } = await supabase
    .from('email_campaigns')
    .update({ status: 'sending', send_immediately: true })
    .eq('id', campaignId);
  if (updateError) throw updateError;

  const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-email-campaign', {
    body: {
      campaignId,
      businessId,
      skipBilling: true,
    },
  });

  if (sendError) {
    await supabase.from('email_campaigns').update({ status: 'draft' }).eq('id', campaignId);
    throw sendError;
  }

  if (!sendResult?.success) {
    await supabase.from('email_campaigns').update({ status: 'draft' }).eq('id', campaignId);
    throw new Error(sendResult?.error || 'Failed to queue campaign');
  }

  const messageType = channelType === 'sms' ? 'SMS messages' : 'Emails';
  alert(
    `Campaign launched successfully! ${sendResult.totalBatches || 1} batch(es) queued. ${messageType} are being sent in the background.`
  );
}

